// src/core/schema/useTableSchema.ts
import { useState, useEffect, useCallback, useRef } from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { arrowTableToObjects, listRelations } from "@/lib";

export interface ColumnStats {
  column_name: string;
  null_count: number;
  top_values: Array<{ value: any; count: number }>;
}

/** Maximum rows sampled per column (unused for null-count). */
const SAMPLE_LIMIT = 1_000;

export function useTableSchema(
  conn: AsyncDuckDBConnection | null,
  table: string | null
) {
  const [schema, setSchema] = useState<any[]>([]);
  const [colNames, setColNames] = useState<string[]>([]);
  const [stats, setStats] = useState<ColumnStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metaReady, setMetaReady] = useState(false);
  const [statsReady, setStatsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // sequence value for cancelation
  const loadSeq = useRef(0);

  const reset = () => {
    setSchema([]);
    setColNames([]);
    setStats([]);
    setMetaReady(false);
    setStatsReady(false);
    setError(null);
  };

  const load = useCallback(async () => {
    const thisSeq = ++loadSeq.current;
    if (!conn || !table) {
      reset();
      return;
    }

    setIsLoading(true);
    setError(null);
    setMetaReady(false);
    setStatsReady(false);

    try {
      // verify table still the same
      const all = await listRelations(conn);
      if (loadSeq.current !== thisSeq || !all.includes(table)) {
        reset();
        return;
      }

      // ── 1️⃣ metadata pass ─────────────────────
      const infoRows = arrowTableToObjects(
        await conn.query(`PRAGMA table_info("${table}")`)
      );
      const meta = infoRows.map((r: any) => ({
        ...r,
        name: r.name || r.NAME,
        type_formatted: r.type || r.TYPE,
      }));
      setSchema(meta);
      const names = meta.map((c) => c.name);
      setColNames(names);
      setMetaReady(true);

      if (loadSeq.current !== thisSeq) return;

      // ── 2️⃣ stats pass ─────────────────────────
      const statsArr: ColumnStats[] = [];
      for (const name of names) {
        if (loadSeq.current !== thisSeq) return;

        // exact null count
        let nullCnt = 0;
        try {
          const row = arrowTableToObjects(
            await conn.query(
              `SELECT COUNT(*) - COUNT("${name}") AS n FROM "${table}"`
            )
          )[0] || { n: 0 };
          nullCnt = Number(row.n);
        } catch {
          nullCnt = 0;
        }

        // up to 5 random non-null examples
        let examples: ColumnStats["top_values"] = [];
        if (nullCnt < Number.MAX_SAFE_INTEGER) {
          try {
            const rows = arrowTableToObjects(
              await conn.query(`
                SELECT DISTINCT "${name}" AS v
                FROM "${table}"
                WHERE "${name}" IS NOT NULL
                ORDER BY random()
                LIMIT 5
              `)
            );
            examples = rows.map((r: any) => ({
              value:
                typeof r.v === "string"
                  ? r.v.length > 20
                    ? r.v.slice(0, 17) + "…"
                    : r.v
                  : typeof r.v === "object"
                  ? JSON.stringify(r.v).slice(0, 17) + "…"
                  : r.v,
              count: 0,
            }));
          } catch {
            examples = [];
          }
        }

        statsArr.push({
          column_name: name,
          null_count: nullCnt,
          top_values: examples,
        });
      }

      if (loadSeq.current === thisSeq) {
        setStats(statsArr);
        setStatsReady(true);
      }
    } catch (e: any) {
      if (loadSeq.current === thisSeq) {
        reset();
        setError(e);
      }
    } finally {
      if (loadSeq.current === thisSeq) {
        setIsLoading(false);
      }
    }
  }, [conn, table]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    schemaData: schema,
    schemaCols: colNames,
    columnStats: stats,
    isLoading,
    metaReady,
    statsReady,
    error,
    reload: load,
  };
}
